/**
 * Bloco #75: status, resumo e filtros do histórico de frequência do paciente.
 * As cores seguem as da agenda (blocos #19/#54).
 */
export type FrequencySession = {
  date: string;
  start: string;
  attendance: string | null;
  canceled: boolean;
  confirmed: boolean;
};

export type FrequencyStatus = 'REALIZADA' | 'FALTA' | 'CANCELADA' | 'CONFIRMADA' | 'PREVISTA' | 'SEM_REGISTRO';

export const FREQUENCY_STATUS: Record<FrequencyStatus, { label: string; tone: string }> = {
  REALIZADA: { label: 'Compareceu', tone: 'bg-green-600 text-white' },
  FALTA: { label: 'Não compareceu', tone: 'bg-rose-600 text-white' },
  CANCELADA: { label: 'Cancelada', tone: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300 line-through' },
  CONFIRMADA: { label: 'Confirmada', tone: 'bg-violet-500 text-white' },
  PREVISTA: { label: 'Prevista', tone: 'bg-white text-slate-700 border border-slate-300 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-600' },
  SEM_REGISTRO: { label: 'Sem registro de presença', tone: 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900' },
};

/** "Hoje" no formato da agenda (YYYY-MM-DD), pelo relógio do aparelho. */
export const todayIso = (now = new Date()): string =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

export function sessionStatus(s: FrequencySession, today: string): FrequencyStatus {
  if (s.attendance === 'Compareceu') return 'REALIZADA';
  if (s.attendance === 'Não Compareceu') return 'FALTA';
  if (s.canceled) return 'CANCELADA';
  if (s.date < today) return 'SEM_REGISTRO';
  return s.confirmed ? 'CONFIRMADA' : 'PREVISTA';
}

export type FrequencySummary = {
  realizadas: number;
  previstas: number;
  faltas: number;
  canceladas: number;
  /** Compareceu ÷ (compareceu + não compareceu); null sem nenhum registro. */
  taxa: number | null;
  primeiraRealizada: string | null;
  ultimaRealizada: string | null;
  proximaPrevista: string | null;
};

export function summarize(sessions: FrequencySession[], today: string): FrequencySummary {
  let realizadas = 0, previstas = 0, faltas = 0, canceladas = 0;
  const feitas: string[] = [];
  const futuras: string[] = [];
  for (const s of sessions) {
    const st = sessionStatus(s, today);
    if (st === 'REALIZADA') { realizadas++; feitas.push(s.date); }
    else if (st === 'FALTA') faltas++;
    else if (st === 'CANCELADA') canceladas++;
    else if (st === 'PREVISTA' || st === 'CONFIRMADA') { previstas++; futuras.push(`${s.date} ${s.start}`); }
  }
  feitas.sort();
  futuras.sort();
  return {
    realizadas,
    previstas,
    faltas,
    canceladas,
    taxa: realizadas + faltas > 0 ? realizadas / (realizadas + faltas) : null,
    primeiraRealizada: feitas[0] ?? null,
    ultimaRealizada: feitas[feitas.length - 1] ?? null,
    proximaPrevista: futuras[0]?.slice(0, 10) ?? null,
  };
}

export type FrequencyFilter = '' | 'previstas' | 'realizadas' | 'faltas' | 'canceladas';

export function filterSessions<T extends FrequencySession>(sessions: T[], today: string, f: { status: FrequencyFilter; from?: string; to?: string }): T[] {
  return sessions.filter((s) => {
    if (f.from && s.date < f.from) return false;
    if (f.to && s.date > f.to) return false;
    const st = sessionStatus(s, today);
    if (f.status === 'previstas') return st === 'PREVISTA' || st === 'CONFIRMADA';
    if (f.status === 'realizadas') return st === 'REALIZADA';
    if (f.status === 'faltas') return st === 'FALTA';
    if (f.status === 'canceladas') return st === 'CANCELADA';
    return true;
  });
}
